import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  BookOpen, ChevronDown, ChevronUp, CircleAlert, Clock, Eye, EyeOff, Layers, ListPlus, Pencil, Play,
  Plus, RefreshCw, Trash2, Upload, Video,
} from 'lucide-react';
import { getLessons, createLesson, updateLesson, deleteLesson, reorderLessons } from '../../api/lessons';
import { getCourseModules } from '../../api/modules';
import { getApiBaseURL, getTokenFromStorage } from '../../api/client';
import FormErrorBoundary from '../../components/FormErrorBoundary/FormErrorBoundary';
import FormErrorDisplay from '../../components/FormErrorDisplay/FormErrorDisplay';
import { getNetworkMonitor } from '../../utils/networkMonitor';
import { unwrapList } from '../../utils/api';
import { formatDuration } from '../../utils/datetime';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import LoadingState from '../../components/LoadingState/LoadingState';
import MetricCard from '../../components/MetricCard/MetricCard';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import Dropdown from '../../components/Dropdown/Dropdown';
import Modal from '../../components/Modal/Modal';
import TableCard from '../../components/TableCard/TableCard';
import { useConfirmDialog } from '../../components/ConfirmDialog/ConfirmDialog';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import { useI18n } from '../../i18n/I18nContext';
import { pluralSuffix } from '../teacher/pluralKey';
import LessonContentField from './LessonContentField';
import ModulePicker from './ModulePicker';
import BulkLessonsForm, { VisibilitySwitch } from './BulkLessonsForm';
import LessonVideoUpload from '../../components/LessonVideoUpload/LessonVideoUpload';
import './CourseLessons.css';

const HLS_JS_CDN = 'https://cdn.jsdelivr.net/npm/hls.js@1.5.7/dist/hls.min.js';
let hlsScriptPromise = null;
function loadHlsJs() {
  if (window.Hls) return Promise.resolve(window.Hls);
  if (hlsScriptPromise) return hlsScriptPromise;
  hlsScriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = HLS_JS_CDN;
    s.async = true;
    s.onload = () =>
      window.Hls ? resolve(window.Hls) : reject(new Error('hls.js loaded but window.Hls is undefined'));
    s.onerror = () => reject(new Error('Failed to load hls.js'));
    document.head.appendChild(s);
  }).catch((err) => {
    // Drop the cached promise on failure: a rejected one would otherwise poison
    // every later attempt in this tab, so a single network blip would kill video
    // playback until a full reload.
    hlsScriptPromise = null;
    throw err;
  });
  return hlsScriptPromise;
}

// Matches a bare YouTube video id (the seeded lesson data stores these directly in videoUrl,
// with no host/protocol at all).
const YOUTUBE_BARE_ID_RE = /^[A-Za-z0-9_-]{11}$/;
// Matches the video id out of a full youtube.com/youtu.be URL, in any of their common shapes.
const YOUTUBE_URL_ID_PATTERNS = [
  /(?:^|\/\/)(?:www\.|m\.)?youtube(?:-nocookie)?\.com\/watch\?[^\s#]*[?&]v=([A-Za-z0-9_-]{11})/,
  /(?:^|\/\/)(?:www\.|m\.)?youtube(?:-nocookie)?\.com\/embed\/([A-Za-z0-9_-]{11})/,
  /(?:^|\/\/)(?:www\.)?youtu\.be\/([A-Za-z0-9_-]{11})/,
];

function extractYouTubeId(videoUrl) {
  if (!videoUrl) return null;
  const trimmed = String(videoUrl).trim();
  if (YOUTUBE_BARE_ID_RE.test(trimmed)) return trimmed;
  for (const pattern of YOUTUBE_URL_ID_PATTERNS) {
    const match = trimmed.match(pattern);
    if (match) return match[1];
  }
  return null;
}

function YouTubeEmbed({ videoId, title }) {
  return (
    <div className="les-youtube">
      <iframe
        src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1`}
        title={title}
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
        allowFullScreen
        frameBorder="0"
      />
    </div>
  );
}

// Picks the right player for a lesson's video: a YouTube embed for a YouTube id/URL (never fed
// to <video src>, which would fire a bogus relative request), HLS for an .m3u8 + media id, or a
// direct <video src> for anything else.
function LessonVideoPlayer({ lesson }) {
  const youTubeId = extractYouTubeId(lesson.videoUrl);
  if (youTubeId) {
    return <YouTubeEmbed videoId={youTubeId} title={lesson.title} />;
  }
  return <HlsVideoPlayer mediaId={lesson.videoMediaId} videoUrl={lesson.videoUrl} />;
}

// The backend mints playlists whose variant/segment URLs are ABSOLUTE — it builds
// them from the request Host (`https://api.darsly.uz/api/v1/...`). In production
// the CRM already talks to that origin, so they resolve as-is. In dev the CRM runs
// on :3000 behind CRA's proxy and `base` is the relative `/api/v1`; there the
// absolute URLs would leave the proxy and be CORS-blocked, so every playlist body
// has to be rewritten back onto `base`.
const ABSOLUTE_API_RE = /https?:\/\/[^/\s]+\/api\/v1\//g;

// Rewriting runs as an hls.js loader rather than by pre-fetching the master into a
// blob: URL. A blob only ever rewrites the master — variant playlists, fetched
// later by hls.js, kept the absolute host — and it costs hls.js its manifest retry
// and error recovery, since a blob cannot be re-requested once revoked.
function makeRewritingLoader(Hls, base) {
  const BaseLoader = Hls.DefaultConfig.loader;
  return class RewritingLoader extends BaseLoader {
    load(context, config, callbacks) {
      const onSuccess = callbacks.onSuccess;
      callbacks.onSuccess = (response, stats, ctx, networkDetails) => {
        if (typeof response.data === 'string') {
          response.data = response.data.replace(ABSOLUTE_API_RE, `${base}/`);
        }
        onSuccess(response, stats, ctx, networkDetails);
      };
      super.load(context, config, callbacks);
    }
  };
}

// Mint a short-lived HLS token for one media id.
//
// The API wraps every payload as `{ success, data, timestamp }`. axios unwraps that
// in a response interceptor, but this is a raw fetch() and gets the envelope — so
// it has to be unwrapped here. Reading `.token` straight off the envelope yields
// undefined, and the player then requests `master.m3u8?token=undefined`, which the
// backend rejects with 401. That is what broke playback in production.
async function mintStreamToken(base, mediaId) {
  const jwt = getTokenFromStorage('accessToken');
  const res = await fetch(`${base}/media/${mediaId}/stream-token`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${jwt}`, 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    const err = new Error(`stream-token failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  const body = await res.json().catch(() => null);
  const token = body?.data?.token ?? body?.token;
  if (!token) throw new Error('stream-token response contained no token');
  return token;
}

function HlsVideoPlayer({ mediaId, videoUrl }) {
  const { t } = useI18n();
  const videoRef = useRef(null);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setError(null);

    const video = videoRef.current;
    if (!video) return undefined;

    let hls = null;
    let cancelled = false;
    let recoveries = 0;
    // hls.js reports a failing variant as a NON-fatal error and just switches to
    // another level. When every level is failing the same way (e.g. the backend
    // 503s on all of them) it loops for minutes and never emits `fatal`, so a
    // fatal-only handler leaves the user staring at a dead player with no message.
    // Track the load failures ourselves and give up deliberately.
    let loadFailures = 0;
    let lastStatus = null;
    let gotPlayableData = false;
    let watchdog = null;

    const fail = (message) => {
      if (!cancelled) setError(message);
    };

    const messageForStatus = (status, fallback) => {
      // 503 is the backend telling us it cannot read the object out of storage —
      // a server-side problem, not a broken link. Say so rather than blaming the
      // browser, so it is not mistaken for an unsupported-format error.
      if (status === 503) return t('courseContent.lessons.videoSourceUnavailable');
      if (status === 401 || status === 403) return t('courseContent.lessons.videoAccessExpired');
      return fallback;
    };

    const setup = async () => {
      const isM3u8 = (videoUrl || '').toLowerCase().includes('.m3u8');
      const base = getApiBaseURL();

      // A media id with no URL at all is an HLS lesson too: the admin import links videoMediaId
      // and leaves videoUrl untouched.
      if (!mediaId || (videoUrl && !isM3u8)) {
        // Not an HLS lesson: play the URL directly, or say there is nothing to play.
        if (videoUrl) {
          video.src = videoUrl;
        } else {
          fail(t('courseContent.lessons.noVideoSource'));
        }
        return;
      }

      let streamToken;
      try {
        streamToken = await mintStreamToken(base, mediaId);
      } catch (e) {
        fail(messageForStatus(e.status, t('courseContent.lessons.videoLoadFailed')));
        return;
      }
      if (cancelled) return;

      const masterUrl = `${base}/media/hls/${mediaId}/master.m3u8?token=${encodeURIComponent(
        streamToken,
      )}`;

      let Hls = null;
      try {
        Hls = await loadHlsJs();
      } catch {
        Hls = null; // fall through to native playback below
      }
      if (cancelled) return;

      if (Hls && Hls.isSupported()) {
        hls = new Hls({ loader: makeRewritingLoader(Hls, base) });
        const giveUp = (details) => {
          // Null the reference as well as destroying it, so the effect cleanup
          // does not call destroy() a second time on a torn-down instance.
          if (hls) {
            hls.destroy();
            hls = null;
          }
          fail(
            messageForStatus(lastStatus, t('courseContent.lessons.hlsError', { details })),
          );
        };

        // Anything that proves bytes are actually flowing clears the watchdog.
        const markProgress = () => {
          gotPlayableData = true;
          loadFailures = 0;
          if (watchdog) {
            clearTimeout(watchdog);
            watchdog = null;
          }
        };
        hls.on(Hls.Events.LEVEL_LOADED, markProgress);
        hls.on(Hls.Events.FRAG_BUFFERED, markProgress);

        hls.on(Hls.Events.ERROR, (_evt, data) => {
          if (data.response?.code) lastStatus = data.response.code;

          if (!data.fatal) {
            // Count only the failures that stop playback starting. Once every
            // level has been tried and failed there is nothing left to switch to,
            // so stop instead of retrying forever.
            const isLoadFailure =
              data.details === Hls.ErrorDetails.LEVEL_LOAD_ERROR ||
              data.details === Hls.ErrorDetails.LEVEL_LOAD_TIMEOUT ||
              data.details === Hls.ErrorDetails.FRAG_LOAD_ERROR ||
              data.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR;
            if (isLoadFailure && !gotPlayableData) {
              // Measured against production: with 4 variants all returning 503,
              // hls.js emits a non-fatal levelLoadError roughly every 1.5s and
              // never escalates to fatal. Giving up once every level has failed
              // once surfaces the problem in ~6s instead of never.
              loadFailures += 1;
              const levels = hls.levels?.length || 1;
              if (loadFailures >= Math.max(levels, 2)) giveUp(data.details);
            }
            return;
          }

          // Retrying only makes sense once something has actually played — a blip
          // mid-stream is worth riding out. A fatal error before the first byte
          // means the stream is not coming, so do not spin on it.
          if (!gotPlayableData) {
            giveUp(data.details);
            return;
          }

          // hls.js rides out most transient faults on its own, but only if asked.
          // Surface an error to the user only once recovery has actually failed.
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR && recoveries < 3) {
            recoveries += 1;
            hls.startLoad();
            return;
          }
          if (data.type === Hls.ErrorTypes.MEDIA_ERROR && recoveries < 3) {
            recoveries += 1;
            hls.recoverMediaError();
            return;
          }
          giveUp(data.details);
        });

        // Backstop: if nothing has become playable at all, stop waiting silently.
        watchdog = setTimeout(() => {
          if (!cancelled && !gotPlayableData) giveUp('stalled');
        }, 15000);

        hls.loadSource(masterUrl);
        hls.attachMedia(video);
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Safari plays HLS natively and fetches the playlists itself, so the
        // rewriting loader does not apply. That is fine: Safari only reaches this
        // branch in production, where the playlist URLs already point at the same
        // API host the CRM is configured with.
        video.src = masterUrl;
      } else {
        fail(t('courseContent.lessons.hlsNotSupported'));
      }
    };

    setup();
    return () => {
      cancelled = true;
      if (watchdog) clearTimeout(watchdog);
      if (hls) hls.destroy();
      // Detach the source so the browser stops downloading a video nobody is watching.
      video.removeAttribute('src');
      video.load();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaId, videoUrl, attempt]);

  // The <video> stays mounted even while the error banner is up. Replacing it with
  // the banner left `videoRef.current` null, so the retry button bumped `attempt`,
  // the effect re-ran, found no element and bailed — retry did nothing at all.
  return (
    <>
      {error && (
        <div className="les-video-error" role="alert">
          <CircleAlert aria-hidden="true" />
          <span>{error}</span>
          <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={() => setAttempt((a) => a + 1)}>
            {t('courseContent.lessons.videoRetry')}
          </button>
        </div>
      )}
      <video
        ref={videoRef}
        controls
        autoPlay
        playsInline
        style={{
          width: '100%',
          maxHeight: '70vh',
          background: 'var(--color-media-bg)',
          display: error ? 'none' : 'block',
        }}
      >
        {t('courseContent.lessons.videoNotSupported')}
      </video>
    </>
  );
}

// A lesson has a video when it carries a URL (YouTube id or hosted file) or an HLS media id. The
// admin import sets only videoMediaId, so checking videoUrl alone showed those lessons as "Pending".
const hasLessonVideo = (lesson) => Boolean(lesson?.videoUrl || lesson?.videoMediaId);

const getLessonOrder = (lesson) => {
  const order = Number(lesson?.orderIndex);
  return Number.isFinite(order) ? order : null;
};

const sortLessonsByOrder = (lessonList) =>
  [...lessonList].sort((a, b) => {
    const aOrder = getLessonOrder(a);
    const bOrder = getLessonOrder(b);

    if (aOrder !== null && bOrder !== null && aOrder !== bOrder) {
      return aOrder - bOrder;
    }

    if (aOrder !== null) return -1;
    if (bOrder !== null) return 1;

    return String(a.title || '').localeCompare(String(b.title || ''));
  });

// A new lesson goes last. Sent without an orderIndex the backend files it under 0, ahead of lesson 1.
const nextLessonOrder = (lessonList) =>
  lessonList.reduce((max, lesson) => Math.max(max, getLessonOrder(lesson) ?? -1), -1) + 1;

// Uzbek never inflects the noun, English only has "1 lesson", Russian has three forms (one, few, many):
// the suffix picks the key of the count's grammatical form ('' = the general one).
const countForm = (lang, count) => {
  const form = pluralSuffix(lang, count);
  return form === 'One' || form === 'Few' ? form : '';
};

// A new lesson starts as a draft: students never see an empty lesson that is still being filled in.
const emptyLessonForm = (moduleId = '') => ({
  title: '',
  description: '',
  moduleId,
  duration: '',
  content: '',
  isPublished: false,
  isSample: false,
});

const isDraft = (lesson) => lesson?.isPublished === false;

// The module's place in the course; students read modules in this order (platform lib/classroom/order.ts).
const moduleOrder = (module) => {
  const n = Number(module?.orderIndex);
  return Number.isFinite(n) ? n : 0;
};

function CourseLessons({ courseId: courseIdProp } = {}) {
  const { t, lang } = useI18n();
  const params = useParams();
  const courseId = courseIdProp || params.courseId || params.id;
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // The first load failed: 'forbidden' | 'failed'. Save, delete and reorder failures use `error`.
  const [loadFailure, setLoadFailure] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [videoLesson, setVideoLesson] = useState(null); // the lesson whose video is being uploaded
  const [formData, setFormData] = useState(emptyLessonForm);
  // The lesson text is sent only when it was touched, so saving a title never rewrites old HTML.
  const [contentDirty, setContentDirty] = useState(false);
  // Remounts the text editor for every lesson the form opens (it is uncontrolled).
  const [formSession, setFormSession] = useState(0);
  const [lastAdded, setLastAdded] = useState(null); // title of the lesson "Save and add another" just made
  const addAnotherRef = useRef(false);
  const [showBulk, setShowBulk] = useState(false);
  const [modulesFailed, setModulesFailed] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // durationDirty: true only when the user explicitly typed in the Duration field.
  // We NEVER pre-fill duration from videoDuration (to avoid lossy minute rounding),
  // so without this flag we cannot distinguish "field is blank because user cleared
  // it" from "field is blank because no override was ever set" — the two must produce
  // different payloads (explicit 0 vs. omit videoDuration entirely).
  const [durationDirty, setDurationDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [moduleMap, setModuleMap] = useState({});
  const [moduleOptions, setModuleOptions] = useState([]);
  const [moduleFilter, setModuleFilter] = useState('all'); // 'all' | 'unassigned' | moduleId
  const [previewLesson, setPreviewLesson] = useState(null); // lesson currently previewed in modal
  // The delete question: `confirm(options)` resolves true/false, `confirmDialog` is rendered once below.
  const [confirm, confirmDialog] = useConfirmDialog();
  const reorderDebounceRef = useRef(null);
  const reorderInProgressRef = useRef(false);
  const pendingReorderRef = useRef(null); // latest order requested while a save was still running
  const titleInputRef = useRef(null);

  const sortedLessons = useMemo(() => sortLessonsByOrder(lessons), [lessons]);

  // NOTE: module labels/filters/counts are sourced only from real module rows
  // (moduleOptions, loaded from getCourseModules) — a lesson with no real
  // moduleId/module association is treated as unassigned everywhere on this
  // page, matching the Edit-lesson form ("— No module —") and the Modules
  // page's real count. Previously this page also synthesized fake "Module N"
  // groupings from lesson order alone, which disagreed with both of those.
  const displayModuleOptions = useMemo(() => {
    const seen = new Set();
    const options = [];

    moduleOptions.forEach((module) => {
      if (!module?.id || seen.has(module.id)) return;
      seen.add(module.id);
      options.push(module);
    });

    return options;
  }, [moduleOptions]);

  const getLessonModuleId = useCallback(
    (lesson) => lesson.moduleId || lesson.module?.id || '',
    [],
  );

  const getLessonModuleTitle = useCallback(
    (lesson) => {
      const explicitModuleId = lesson.moduleId || lesson.module?.id;
      return lesson.module?.title || moduleMap[explicitModuleId] || '';
    },
    [moduleMap],
  );

  // Form validation state
  const [formErrors, setFormErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [formRetryCount, setFormRetryCount] = useState(0);
  const [formRetryError, setFormRetryError] = useState(null);

  const networkMonitorRef = useRef(null);

  // Debounce timer for double-click prevention
  const submitDebounceRef = useRef(null);

  // One loader for the first load and every reload. Only the newest request may write state, so a
  // slow earlier response cannot overwrite a newer list. Reloads after a save, a delete or a
  // failed reorder are `silent`: the table stays on screen (no skeleton flash, no lost scroll).
  const loadSeqRef = useRef(0);
  const fetchLessons = useCallback(async ({ silent = false } = {}) => {
    const seq = loadSeqRef.current + 1;
    loadSeqRef.current = seq;
    if (!silent) setLoading(true);
    try {
      const result = await getLessons(courseId);
      if (seq !== loadSeqRef.current) return;
      setLessons(unwrapList(result));
      setError(null);
      setLoadFailure(null);
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      const forbidden = err?.code === 403;
      if (silent) {
        // The table is still there: say what went wrong above it.
        setError(forbidden ? t('courseContent.lessons.accessDenied') : t('courseContent.lessons.loadError'));
      } else {
        setLoadFailure(forbidden ? 'forbidden' : 'failed');
      }
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [courseId, t]);

  useEffect(() => {
    fetchLessons();
    return () => { loadSeqRef.current += 1; };
  }, [fetchLessons]);

  // The course's modules: names for the list, groups, and the module pickers. A failed load says so
  // in the pickers (an empty list would look like a course without modules).
  const loadModules = useCallback(async () => {
    if (!courseId) return;
    try {
      const list = unwrapList(await getCourseModules(courseId, { limit: 100 }));
      setModuleOptions(list);
      const map = {};
      list.forEach(m => { map[m.id] = m.title || m.name || t('courseContent.lessons.unnamedModule'); });
      setModuleMap(map);
      setModulesFailed(false);
    } catch {
      setModulesFailed(true);
    }
  }, [courseId, t]);

  useEffect(() => { loadModules(); }, [loadModules]);

  // A duration typed by hand, in minutes: "45 daqiqa", "2 soat 15 daqiqa".
  const formatManualDuration = (mins) => {
    if (mins === null || mins === undefined || mins === '') return '—';
    const n = Number(mins);
    if (!Number.isFinite(n)) return '—';
    if (n < 60) return t('courseContent.lessons.durationMinutes', { count: n });
    return formatDuration(n * 60, { style: 'long' });
  };

  // The video's own length (seconds, read from the file) as a clock: "3:20", "1:05:00".
  const formatVideoDuration = (seconds) => {
    if (seconds === null || seconds === undefined || seconds === '') return '—';
    if (!Number.isFinite(Number(seconds))) return '—';
    return formatDuration(seconds);
  };

  const formatLessonDuration = (lesson) => {
    if (lesson.videoDuration !== null && lesson.videoDuration !== undefined && lesson.videoDuration !== '') {
      return formatVideoDuration(lesson.videoDuration);
    }

    return formatManualDuration(lesson.duration);
  };

  // Opening the form (or switching it to another lesson) puts the cursor in the title field. The
  // browser scrolls a focused field into view, so on a phone the form is never left off-screen.
  useEffect(() => {
    if (showForm && titleInputRef.current) titleInputRef.current.focus();
  }, [showForm, editingId, formSession]);

  // Setup network monitoring
  useEffect(() => {
    networkMonitorRef.current = getNetworkMonitor();

    const unsubscribe = networkMonitorRef.current.onReconnect(() => {
      if (formRetryError && formRetryCount < 3) {
        setFormRetryError(null);
        setFormRetryCount(0);
      }
    });

    return () => unsubscribe();
  }, [formRetryError, formRetryCount]);

  // SECURITY: Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      if (submitDebounceRef.current) {
        clearTimeout(submitDebounceRef.current);
        submitDebounceRef.current = null;
      }
      if (reorderDebounceRef.current) {
        clearTimeout(reorderDebounceRef.current);
        reorderDebounceRef.current = null;
      }
    };
  }, []);

  // Validation rules stored in a ref so their identity is stable across renders.
  const validationRulesRef = useRef({
    title: {
      required: true,
      minLength: 3,
      maxLength: 100,
      labelKey: 'titleFieldLabel'
    },
    duration: {
      validate: (value) => {
        if (value && (Number(value) < 0 || Number(value) > 10000)) {
          return 'durationRange';
        }
        return null;
      },
      labelKey: 'durationFieldLabel'
    }
  });

  const validateLessonField = useCallback((fieldName, value) => {
    const rule = validationRulesRef.current[fieldName];
    if (!rule) return null;

    const label = t(`courseContent.lessons.${rule.labelKey}`);

    if (rule.required && !value) {
      return t('courseContent.lessons.fieldRequired', { field: label });
    }

    if (value && rule.minLength && value.length < rule.minLength) {
      return t('courseContent.lessons.minLength', { field: label, min: rule.minLength });
    }

    if (value && rule.maxLength && value.length > rule.maxLength) {
      return t('courseContent.lessons.maxLength', { field: label, max: rule.maxLength });
    }

    if (value && rule.validate) {
      const errorKey = rule.validate(value);
      return errorKey ? t(`courseContent.lessons.${errorKey}`) : null;
    }

    return null;
  }, [t]);

  const handleFieldBlur = useCallback((fieldName) => {
    setTouchedFields(prev => ({
      ...prev,
      [fieldName]: true
    }));

    const error = validateLessonField(fieldName, formData[fieldName]);
    setFormErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
  }, [formData, validateLessonField]);

  const isFormValid = useCallback(() => {
    const errors = {};
    Object.keys(validationRulesRef.current).forEach(fieldName => {
      const error = validateLessonField(fieldName, formData[fieldName]);
      if (error) errors[fieldName] = error;
    });
    return Object.keys(errors).length === 0;
  }, [formData, validateLessonField]);

  // `moduleId`: the group whose "Add lesson" was pressed; otherwise the module being filtered on.
  const handleAddClick = (moduleId) => {
    const presetModule = typeof moduleId === 'string'
      ? moduleId
      : (moduleFilter !== 'all' && moduleFilter !== 'unassigned' ? moduleFilter : '');
    setEditingId(null);
    setShowBulk(false);
    setFormData(emptyLessonForm(presetModule));
    setDurationDirty(false);
    setContentDirty(false);
    setLastAdded(null);
    setFormSession((n) => n + 1);
    // Reset validation state left over from a previous (cancelled or completed) form session,
    // otherwise a fresh, untouched "New Lesson" form can open already showing a stale
    // "required" error from whatever was last touched/invalid.
    setFormErrors({});
    setTouchedFields({});
    setShowForm(true);
  };

  const openBulk = () => {
    setShowForm(false);
    setEditingId(null);
    setShowBulk(true);
  };

  const handleEditClick = (lesson) => {
    setEditingId(lesson.id);
    // IMPORTANT: the Duration field is intentionally left BLANK when opening Edit, even
    // when the lesson already has a videoDuration. Reasons:
    // 1. videoDuration is in SECONDS (auto-detected). Converting to minutes is lossy
    //    (Math.round(200s/60) = 3, then 3*60 = 180s — 20-second data loss on silent Save).
    // 2. The exact video length is shown as read-only hint text below the field (m:ss).
    // 3. durationDirty is only set when the user actually types; handleSubmit skips
    //    videoDuration in the payload when durationDirty is false, so pressing Save
    //    without touching Duration leaves the server value untouched.
    setShowBulk(false);
    setFormData({
      title: lesson.title,
      description: lesson.description || '',
      moduleId: lesson.moduleId || lesson.module?.id || '',
      duration: '',
      content: lesson.content || '',
      isPublished: !isDraft(lesson),
      isSample: Boolean(lesson.isSample),
    });
    setDurationDirty(false);
    setContentDirty(false);
    setLastAdded(null);
    setFormSession((n) => n + 1);
    // Reset validation state left over from a previous form session (see handleAddClick).
    setFormErrors({});
    setTouchedFields({});
    setShowForm(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Track that the user intentionally typed in the Duration field.
    // Only when durationDirty is true will handleSubmit include videoDuration in the
    // payload — this prevents the lossy round-trip (open Edit → Save without touching
    // duration → server value silently overwritten with a rounded minute value).
    if (name === 'duration') {
      setDurationDirty(true);
    }

    // Update validation on change if field was touched
    if (touchedFields[name]) {
      const error = validateLessonField(name, value);
      setFormErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    // Validate form before submission
    if (!isFormValid()) {
      setTouchedFields({
        title: true,
        duration: true
      });
      return;
    }

    // Debounce: prevent rapid successive submissions
    if (submitDebounceRef.current) {
      clearTimeout(submitDebounceRef.current);
    }

    setSaving(true);
    setError(null);
    setFormRetryError(null);
    setFormRetryCount(0);

    const addAnother = addAnotherRef.current && !editingId;
    addAnotherRef.current = false;

    submitDebounceRef.current = setTimeout(async () => {
      try {
        // videoDuration is only sent when the user explicitly changed the Duration field
        // (durationDirty === true). Without this guard, opening Edit and pressing Save
        // without touching Duration would convert the empty field to undefined — but
        // before round 5 the field was pre-filled with Math.round(videoDuration/60),
        // causing lossy rounding (200s → 180s) on every untouched Save. Now the field
        // always opens empty; durationDirty is set on first keystroke; and the payload
        // only carries videoDuration when the user deliberately set it.
        // moduleId is always included ('' → null so the backend can clear an assignment).
        const submitData = {
          title: formData.title,
          description: formData.description || undefined,
          moduleId: formData.moduleId || null,
          isPublished: formData.isPublished,
          isSample: formData.isSample,
        };
        if (contentDirty || (!editingId && formData.content)) {
          submitData.content = formData.content;
        }
        if (durationDirty) {
          const durationMinutes = formData.duration !== '' && formData.duration !== null && formData.duration !== undefined
            ? parseInt(formData.duration, 10)
            : null;
          submitData.videoDuration = durationMinutes !== null && !Number.isNaN(durationMinutes)
            ? durationMinutes * 60
            : undefined;
        }

        if (editingId) {
          await updateLesson(courseId, editingId, submitData);
        } else {
          await createLesson(courseId, { ...submitData, orderIndex: nextLessonOrder(lessons) });
        }

        await fetchLessons({ silent: true });
        if (addAnother) {
          // The next lesson keeps the module and the status; the title, text and length start empty.
          setLastAdded(formData.title);
          setFormData((prev) => ({ ...emptyLessonForm(prev.moduleId), isPublished: prev.isPublished, isSample: prev.isSample }));
          setFormSession((n) => n + 1);
        } else {
          setShowForm(false);
          setFormData(emptyLessonForm());
          setLastAdded(null);
        }
        setDurationDirty(false);
        setContentDirty(false);
        setFormErrors({});
        setTouchedFields({});
      } catch (err) {
        // The page's own words, never the server's (often English) text.
        setError(t('courseContent.lessons.saveError'));
        setFormRetryError(err);
      } finally {
        setSaving(false);
      }
    }, 300);
  }, [courseId, editingId, formData, durationDirty, contentDirty, fetchLessons, isFormValid, lessons, t]);

  const handleDelete = useCallback(async (lessonId) => {
    const confirmed = await confirm({
      title: t('courseContent.lessons.confirmDeleteTitle'),
      description: t('courseContent.lessons.confirmDeleteHint'),
      confirmLabel: t('courseContent.lessons.delete'),
      tone: 'danger',
    });
    if (!confirmed) return;

    setSaving(true);
    setError(null);

    try {
      await deleteLesson(courseId, lessonId);
      await fetchLessons({ silent: true });
    } catch (err) {
      setError(t('courseContent.lessons.deleteError'));
    } finally {
      setSaving(false);
    }
  }, [confirm, courseId, fetchLessons, t]);

  const setLessonPublished = useCallback(async (lesson, isPublished) => {
    setError(null);
    try {
      await updateLesson(courseId, lesson.id, { isPublished });
      await fetchLessons({ silent: true });
    } catch {
      setError(t('lessonAuthoring.row.statusFailed'));
    }
  }, [courseId, fetchLessons, t]);

  const drafts = useMemo(() => lessons.filter(isDraft), [lessons]);

  const publishAllDrafts = useCallback(async () => {
    const confirmed = await confirm({
      title: t('lessonAuthoring.row.publishAllTitle', { count: drafts.length }),
      description: t('lessonAuthoring.row.publishAllHint'),
      confirmLabel: t('lessonAuthoring.row.publish'),
    });
    if (!confirmed) return;
    setSaving(true);
    setError(null);
    try {
      for (const lesson of drafts) {
        // eslint-disable-next-line no-await-in-loop
        await updateLesson(courseId, lesson.id, { isPublished: true });
      }
    } catch {
      setError(t('lessonAuthoring.row.statusFailed'));
    } finally {
      await fetchLessons({ silent: true });
      setSaving(false);
    }
  }, [confirm, courseId, drafts, fetchLessons, t]);

  // Other sections link here with ?add=1 ("add a lesson first"): open the add form once the list is in.
  useEffect(() => {
    if (loading || searchParams.get('add') !== '1') return;
    const next = new URLSearchParams(searchParams);
    next.delete('add');
    setSearchParams(next, { replace: true });
    handleAddClick('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, searchParams]);

  const filteredLessons = (() => {
    if (moduleFilter === 'all') return sortedLessons;
    if (moduleFilter === 'unassigned') return sortedLessons.filter((l) => !getLessonModuleId(l));
    return sortedLessons.filter((l) => getLessonModuleId(l) === moduleFilter);
  })();

  // `reorderedAll` is the whole course, renumbered by moveLesson, so its ids are exactly what the
  // backend's reorder route wants. (The old per-row diff compared the already-renumbered rows with
  // themselves, found nothing to change and never sent a request.)
  const persistReorder = useCallback(
    async (reorderedAll) => {
      if (reorderInProgressRef.current) {
        // Send the newest order as soon as the running save finishes instead of dropping it.
        pendingReorderRef.current = reorderedAll;
        return;
      }
      reorderInProgressRef.current = true;
      try {
        let next = reorderedAll;
        while (next) {
          pendingReorderRef.current = null;
          await reorderLessons(courseId, next.map((l) => l.id));
          next = pendingReorderRef.current;
        }
        setError(null);
      } catch (err) {
        pendingReorderRef.current = null;
        // Put the server's order back first: a successful reload clears the error banner.
        await fetchLessons({ silent: true });
        setError(t('courseContent.lessons.reorderError'));
      } finally {
        reorderInProgressRef.current = false;
      }
    },
    [courseId, fetchLessons, t],
  );

  const moveLesson = useCallback(
    (lessonId, direction, visible) => {
      // Find positions inside the list the row sits in (its module group, or the filtered list), but
      // persist global indices.
      const visIndex = visible.findIndex((l) => l.id === lessonId);
      if (visIndex < 0) return;
      const swapWith = direction === 'up' ? visible[visIndex - 1] : visible[visIndex + 1];
      if (!swapWith) return;

      const next = [...sortedLessons];
      const i1 = next.findIndex((l) => l.id === lessonId);
      const i2 = next.findIndex((l) => l.id === swapWith.id);
      if (i1 < 0 || i2 < 0) return;
      [next[i1], next[i2]] = [next[i2], next[i1]];
      const renumbered = next.map((l, i) => ({ ...l, orderIndex: i }));
      setLessons(renumbered);

      if (reorderDebounceRef.current) clearTimeout(reorderDebounceRef.current);
      reorderDebounceRef.current = setTimeout(() => {
        persistReorder(renumbered);
      }, 300);
    },
    [sortedLessons, persistReorder],
  );

  // The list in the order students read it: a group per module (module order), lessons by their own
  // order inside it, lessons without a module first. Only when nothing is filtered and the course
  // has modules; otherwise one group without a header.
  const lessonGroups = useMemo(() => {
    if (moduleFilter !== 'all' || displayModuleOptions.length === 0) {
      return [{ key: 'flat', moduleId: null, title: null, lessons: filteredLessons }];
    }
    const known = new Set(displayModuleOptions.map((m) => m.id));
    const unassigned = sortedLessons.filter((l) => !known.has(getLessonModuleId(l)));
    const groups = [...displayModuleOptions]
      .sort((a, b) => moduleOrder(a) - moduleOrder(b))
      .map((m) => ({
        key: m.id,
        moduleId: m.id,
        title: m.title || m.name || t('courseContent.lessons.unnamedModule'),
        lessons: sortedLessons.filter((l) => getLessonModuleId(l) === m.id),
      }));
    return unassigned.length > 0
      ? [{ key: 'none', moduleId: '', title: t('lessonAuthoring.module.noModuleGroup'), lessons: unassigned }, ...groups]
      : groups;
  }, [moduleFilter, displayModuleOptions, filteredLessons, sortedLessons, getLessonModuleId, t]);

  const closePreview = useCallback(() => setPreviewLesson(null), []);
  const closeVideoUpload = useCallback(() => setVideoLesson(null), []);
  // The new video is live: the row, the counts and the preview use it at once (no reload needed).
  const handleVideoReady = useCallback((updated) => {
    setLessons((list) => list.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)));
    setVideoLesson((current) => (current && current.id === updated.id ? { ...current, ...updated } : current));
  }, []);

  if (loading) {
    return (
      <div className="course-lessons">
        <LoadingState skeleton="table" rows={8} message={t('courseContent.lessons.loadingMessage')} />
      </div>
    );
  }

  if (loadFailure) {
    return (
      <div className="course-lessons">
        {loadFailure === 'forbidden' ? (
          <PageEmptyState variant="forbidden" size="sm" title={t('courseContent.lessons.accessDenied')} />
        ) : (
          <PageEmptyState variant="error" size="sm" onRetry={() => fetchLessons()} />
        )}
      </div>
    );
  }

  // KPI numbers. Length is the video's own seconds when the file reported them, else the minutes
  // someone typed; both are summed in seconds and shown once as a clock.
  const withVideo = lessons.filter(hasLessonVideo).length;
  const totalSeconds = lessons.reduce((sum, l) => {
    if (l.videoDuration !== null && l.videoDuration !== undefined && l.videoDuration !== '') {
      return sum + (Number(l.videoDuration) || 0);
    }
    return sum + (Number(l.duration || l.durationMinutes || 0) || 0) * 60;
  }, 0);

  const titleMessage = touchedFields.title && formErrors.title ? formErrors.title : null;
  const durationMessage = touchedFields.duration && formErrors.duration ? formErrors.duration : null;
  const editingLesson = editingId ? lessons.find((l) => l.id === editingId) : null;
  const hasEditingVideoLength = Boolean(editingLesson)
    && editingLesson.videoDuration !== null
    && editingLesson.videoDuration !== undefined
    && editingLesson.videoDuration !== '';

  return (
    <FormErrorBoundary>
      <div className="course-lessons">
        <div className="les-toolbar">
          <div className="les-toolbar-main">
            <h2 className="les-title">{t('courseContent.lessons.pageTitle')}</h2>
            <span className="ds-badge ds-badge-neutral">
              {t(`courseContent.lessons.lessonCount${countForm(lang, lessons.length)}`, { count: lessons.length })}
            </span>
          </div>
          {/* An empty course has nothing to filter, and its empty state already carries the one
              yellow "add" button, so the toolbar offers neither. */}
          {lessons.length > 0 && (
            <div className="les-toolbar-actions">
              <Dropdown
                className="les-filter"
                size="sm"
                value={moduleFilter}
                onChange={setModuleFilter}
                ariaLabel={t('courseContent.lessons.filterAriaLabel')}
                options={[
                  { value: 'all', label: t('courseContent.lessons.filterAllModules') },
                  { value: 'unassigned', label: t('courseContent.lessons.filterNoModule'), separator: true },
                  ...displayModuleOptions.map((m) => ({
                    value: m.id,
                    label: m.title || m.name || t('courseContent.lessons.unnamedModule'),
                  })),
                ]}
              />
              {drafts.length > 0 && (
                <button type="button" onClick={publishAllDrafts} className="ds-btn ds-btn-secondary" disabled={saving}>
                  <Eye aria-hidden="true" />
                  {t('lessonAuthoring.row.publishAll', { count: drafts.length })}
                </button>
              )}
              <button type="button" onClick={openBulk} className="ds-btn ds-btn-secondary">
                <ListPlus aria-hidden="true" />
                {t('lessonAuthoring.bulk.open')}
              </button>
              <button type="button" onClick={() => handleAddClick()} className="ds-btn ds-btn-primary les-add-btn">
                <Plus aria-hidden="true" />
                {t('courseContent.lessons.addLesson')}
              </button>
            </div>
          )}
        </div>

        {error && !formRetryError && (
          <div className="ui-alert" role="alert">
            <div>{error}</div>
          </div>
        )}

        {showForm && (
          <section className="section-card les-form-card" aria-labelledby="les-form-title">
            <div className="section-head">
              <h3 className="section-head__title" id="les-form-title">
                {editingId ? t('courseContent.lessons.editLesson') : t('courseContent.lessons.newLesson')}
              </h3>
            </div>

            {formRetryError && (
              <FormErrorDisplay
                error={formRetryError}
                onRetry={handleSubmit}
                retryCount={formRetryCount}
                maxRetries={3}
                isRetrying={saving}
              />
            )}

            <form onSubmit={handleSubmit} className="les-form">
              <div className="ui-field les-field">
                <label className="ui-label" htmlFor="les-title-input">
                  {t('courseContent.lessons.titleLabel')}
                  <span className="ui-req" aria-hidden="true">*</span>
                </label>
                <input
                  id="les-title-input"
                  ref={titleInputRef}
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleFormChange}
                  onBlur={() => handleFieldBlur('title')}
                  aria-invalid={formErrors.title ? 'true' : undefined}
                  aria-required="true"
                  aria-describedby="les-title-msg"
                  maxLength="100"
                  placeholder={t('courseContent.lessons.titlePlaceholder')}
                />
                {/* A fixed-height message slot: an error that appears on blur must not move the
                    buttons below it between mousedown and mouseup on Save/Cancel, or the browser
                    swallows that first click. */}
                <div className="les-msg" id="les-title-msg">
                  {titleMessage && <p className="ui-error" role="alert">{titleMessage}</p>}
                </div>
              </div>
              <div className="ui-field les-field">
                <label className="ui-label" htmlFor="les-desc-input">{t('courseContent.lessons.descriptionLabel')}</label>
                <textarea
                  id="les-desc-input"
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  maxLength="500"
                  rows="3"
                  placeholder={t('courseContent.lessons.descriptionPlaceholder')}
                />
              </div>
              <div className="ui-field les-field">
                <label className="ui-label" htmlFor="les-content-input">{t('lessonAuthoring.content.label')}</label>
                <LessonContentField
                  key={formSession}
                  id="les-content-input"
                  initialHtml={formData.content}
                  onChange={(html) => {
                    setContentDirty(true);
                    setFormData((prev) => ({ ...prev, content: html }));
                  }}
                />
              </div>
              <div className="ui-form-grid les-form-row">
                <div className="ui-field les-field">
                  <span className="ui-label" id="les-module-label">{t('courseContent.lessons.moduleLabel')}</span>
                  <ModulePicker
                    courseId={courseId}
                    modules={displayModuleOptions}
                    value={formData.moduleId}
                    onChange={(val) => handleFormChange({ target: { name: 'moduleId', value: val } })}
                    onCreated={loadModules}
                    loadFailed={modulesFailed}
                    onRetryLoad={loadModules}
                    labelledBy="les-module-label"
                  />
                </div>
                <div className="ui-field les-field">
                  <label className="ui-label" htmlFor="les-duration-input">{t('courseContent.lessons.durationLabel')}</label>
                  <input
                    id="les-duration-input"
                    type="number"
                    inputMode="numeric"
                    name="duration"
                    value={formData.duration}
                    onChange={handleFormChange}
                    onBlur={() => handleFieldBlur('duration')}
                    aria-invalid={formErrors.duration ? 'true' : undefined}
                    aria-describedby="les-duration-msg"
                    min="0"
                    placeholder={t('courseContent.lessons.durationPlaceholder')}
                  />
                  {hasEditingVideoLength && (
                    <span className="ui-hint">
                      {t('courseContent.lessons.durationVideoHint', { duration: formatVideoDuration(editingLesson.videoDuration) })}
                    </span>
                  )}
                  <div className="les-msg" id="les-duration-msg">
                    {durationMessage && <p className="ui-error" role="alert">{durationMessage}</p>}
                  </div>
                </div>
              </div>
              <div className="ui-form-grid les-form-row">
                <div className="ui-field les-field">
                  <span className="ui-label" id="les-status-label">{t('lessonAuthoring.visibility.label')}</span>
                  <VisibilitySwitch
                    value={formData.isPublished}
                    onChange={(isPublished) => setFormData((prev) => ({ ...prev, isPublished }))}
                    labelledBy="les-status-label"
                  />
                </div>
                <div className="ui-field les-field">
                  <label className="les-check">
                    <input
                      type="checkbox"
                      checked={formData.isSample}
                      onChange={(e) => setFormData((prev) => ({ ...prev, isSample: e.target.checked }))}
                    />
                    <span>
                      <span className="les-check-label">{t('lessonAuthoring.sample.label')}</span>
                      <span className="ui-hint">{t('lessonAuthoring.sample.hint')}</span>
                    </span>
                  </label>
                </div>
              </div>
              <div className="les-form-actions">
                {lastAdded && (
                  <span className="les-added" role="status">{t('lessonAuthoring.added', { title: lastAdded })}</span>
                )}
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="ds-btn ds-btn-secondary"
                >
                  {t('courseContent.lessons.cancel')}
                </button>
                {!editingId && (
                  <button
                    type="submit"
                    disabled={saving || !isFormValid()}
                    className="ds-btn ds-btn-secondary"
                    onClick={() => { addAnotherRef.current = true; }}
                  >
                    {t('lessonAuthoring.saveAndAdd')}
                  </button>
                )}
                <button
                  type="submit"
                  disabled={saving || !isFormValid()}
                  className="ds-btn ds-btn-primary"
                  onClick={() => { addAnotherRef.current = false; }}
                >
                  {saving ? t('courseContent.lessons.saving') : t('courseContent.lessons.save')}
                </button>
              </div>
            </form>
          </section>
        )}

        {showBulk && (
          <BulkLessonsForm
            courseId={courseId}
            modules={displayModuleOptions}
            defaultModuleId={moduleFilter !== 'all' && moduleFilter !== 'unassigned' ? moduleFilter : ''}
            startOrder={nextLessonOrder(lessons)}
            onModuleCreated={loadModules}
            modulesFailed={modulesFailed}
            onRetryModules={loadModules}
            onCancel={() => setShowBulk(false)}
            onDone={async (count) => {
              setShowBulk(false);
              await fetchLessons({ silent: true });
              toast.success(t('lessonAuthoring.bulk.done', { count }));
            }}
          />
        )}

        {lessons.length > 0 && (
          <div className="kpi-row">
            <MetricCard
              tone="brand"
              title={t('courseContent.lessons.statLessons')}
              value={lessons.length}
              icon={<BookOpen aria-hidden="true" />}
            />
            <MetricCard
              tone="success"
              title={t('courseContent.lessons.statWithVideo')}
              value={withVideo}
              icon={<Video aria-hidden="true" />}
            />
            <MetricCard
              tone="info"
              title={t('courseContent.lessons.statTotalDuration')}
              value={formatDuration(totalSeconds)}
              countUp={false}
              icon={<Clock aria-hidden="true" />}
            />
            <MetricCard
              tone="purple"
              title={t('courseContent.lessons.statModules')}
              value={displayModuleOptions.length}
              icon={<Layers aria-hidden="true" />}
            />
          </div>
        )}

        {filteredLessons.length === 0 ? (
          lessons.length === 0 ? (
            <PageEmptyState
              variant="docs"
              title={t('courseContent.lessons.emptyTitle')}
              description={t('courseContent.lessons.emptyDescription')}
              actionLabel={t('courseContent.lessons.addLesson')}
              onAction={() => handleAddClick()}
              secondaryLabel={t('lessonAuthoring.bulk.open')}
              onSecondaryAction={openBulk}
            />
          ) : (
            <PageEmptyState
              variant="search"
              title={t('courseContent.lessons.emptyFilterTitle')}
              description={t('courseContent.lessons.emptyFilterDescription')}
              secondaryLabel={t('courseContent.lessons.clearFilter')}
              onSecondaryAction={() => setModuleFilter('all')}
            />
          )
        ) : (
          <TableCard className="les-card" aria-label={t('courseContent.lessons.pageTitle')}>
            <table className="les-table ui-table">
              <thead>
                <tr>
                  <th className="les-col-num">{t('courseContent.lessons.colNumber')}</th>
                  <th>{t('courseContent.lessons.colLesson')}</th>
                  <th>{t('courseContent.lessons.colModule')}</th>
                  <th className="les-col-duration">{t('courseContent.lessons.colDuration')}</th>
                  <th>{t('courseContent.lessons.colVideo')}</th>
                  <th className="les-col-order">{t('courseContent.lessons.colOrder')}</th>
                  <th className="les-col-actions">{t('courseContent.lessons.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {lessonGroups.map((group, gi) => {
                  const groupStart = lessonGroups.slice(0, gi).reduce((n, g) => n + g.lessons.length, 0);
                  return (
                    <React.Fragment key={group.key}>
                      {group.title !== null && (
                        <tr className="les-group-row">
                          <td colSpan={7}>
                            <div className="les-group-head">
                              <span className="les-group-title">{group.title}</span>
                              <span className="ds-badge ds-badge-neutral">
                                {t('lessonAuthoring.module.groupCount', { count: group.lessons.length })}
                              </span>
                              <button
                                type="button"
                                className="ds-btn ds-btn-secondary ds-btn-sm les-group-add"
                                aria-label={t('lessonAuthoring.module.addToGroupAria', { module: group.title })}
                                onClick={() => handleAddClick(group.moduleId)}
                              >
                                <Plus aria-hidden="true" />
                                {t('lessonAuthoring.module.addToGroup')}
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                      {group.lessons.map((lesson) => {
                    const idx = group.lessons.indexOf(lesson);
                    const number = groupStart + idx + 1;
                    const hasVideo = hasLessonVideo(lesson);
                    const draft = isDraft(lesson);
                    const moduleTitle = getLessonModuleTitle(lesson);
                    const menuItems = [
                      {
                        key: 'video',
                        label: hasVideo
                          ? t('courseContent.lessons.menuReplaceVideo')
                          : t('courseContent.lessons.menuAttachVideo'),
                        icon: hasVideo ? <RefreshCw aria-hidden="true" /> : <Upload aria-hidden="true" />,
                        onSelect: () => setVideoLesson(lesson),
                      },
                      {
                        key: 'publish',
                        label: draft ? t('lessonAuthoring.row.publish') : t('lessonAuthoring.row.unpublish'),
                        icon: draft ? <Eye aria-hidden="true" /> : <EyeOff aria-hidden="true" />,
                        onSelect: () => setLessonPublished(lesson, draft),
                      },
                      {
                        key: 'delete',
                        label: t('courseContent.lessons.delete'),
                        icon: <Trash2 aria-hidden="true" />,
                        tone: 'danger',
                        onSelect: () => handleDelete(lesson.id),
                      },
                    ];
                    return (
                      <tr key={lesson.id}>
                        <td className="les-col-num les-cell-num">{number}</td>
                        <td className="les-cell-lesson">
                          <span className="les-lesson-title">
                            {lesson.title}
                            {draft && <span className="ds-badge ds-badge-warning les-flag">{t('lessonAuthoring.row.draft')}</span>}
                            {lesson.isSample && <span className="ds-badge ds-badge-info les-flag">{t('lessonAuthoring.row.sample')}</span>}
                          </span>
                          {lesson.description && (
                            <span className="les-lesson-desc">
                              {lesson.description.substring(0, 80)}{lesson.description.length > 80 ? '…' : ''}
                            </span>
                          )}
                        </td>
                        <td className="les-cell-module" data-label={t('courseContent.lessons.colModule')}>
                          {moduleTitle ? (
                            <span className="ds-badge ds-badge-purple les-module-badge" title={moduleTitle}>
                              {moduleTitle}
                            </span>
                          ) : displayModuleOptions.length === 0 ? (
                            <span className="les-muted" title={t('courseContent.lessons.noModuleTooltip')}>
                              {t('courseContent.lessons.noModule')}
                            </span>
                          ) : (
                            <span className="les-muted" title={t('courseContent.lessons.unassignedTooltip')}>
                              {t('courseContent.lessons.unassigned')}
                            </span>
                          )}
                        </td>
                        <td className="les-col-duration les-cell-duration" data-label={t('courseContent.lessons.colDuration')}>
                          {formatLessonDuration(lesson)}
                        </td>
                        <td className="les-cell-video" data-label={t('courseContent.lessons.colVideo')}>
                          {hasVideo ? (
                            <StatusBadge status="uploaded">{t('courseContent.lessons.videoStatusUploaded')}</StatusBadge>
                          ) : (
                            <button
                              type="button"
                              className="ds-btn ds-btn-secondary ds-btn-sm les-video-add"
                              onClick={() => setVideoLesson(lesson)}
                              aria-label={t('lessonAuthoring.row.addVideoAria', { title: lesson.title })}
                            >
                              <Upload aria-hidden="true" />
                              {t('lessonAuthoring.row.addVideo')}
                            </button>
                          )}
                        </td>
                        <td className="les-col-order les-cell-order">
                          <div className="ui-row-actions">
                            <button
                              type="button"
                              className="ui-icon-btn"
                              title={t('courseContent.lessons.moveUp')}
                              aria-label={t('courseContent.lessons.moveUp')}
                              onClick={() => moveLesson(lesson.id, 'up', group.lessons)}
                              disabled={idx === 0}
                            >
                              <ChevronUp aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className="ui-icon-btn"
                              title={t('courseContent.lessons.moveDown')}
                              aria-label={t('courseContent.lessons.moveDown')}
                              onClick={() => moveLesson(lesson.id, 'down', group.lessons)}
                              disabled={idx === group.lessons.length - 1}
                            >
                              <ChevronDown aria-hidden="true" />
                            </button>
                          </div>
                        </td>
                        <td className="les-col-actions les-cell-actions">
                          <div className="ui-row-actions">
                            {hasVideo && (
                              <button
                                type="button"
                                onClick={() => setPreviewLesson(lesson)}
                                className="ui-icon-btn"
                                title={t('courseContent.lessons.previewVideoTitle')}
                                aria-label={t('courseContent.lessons.previewVideoTitle')}
                              >
                                <Play aria-hidden="true" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleEditClick(lesson)}
                              className="ui-icon-btn"
                              title={t('courseContent.lessons.editLessonTitle')}
                              aria-label={t('courseContent.lessons.editLessonTitle')}
                            >
                              <Pencil aria-hidden="true" />
                            </button>
                            <ActionMenu ariaLabel={t('courseContent.lessons.moreActions')} items={menuItems} />
                          </div>
                        </td>
                      </tr>
                    );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </TableCard>
        )}

        {previewLesson && (
          <Modal open onClose={closePreview} title={previewLesson.title} size="xl">
            <div className="les-dialog-media">
              {hasLessonVideo(previewLesson) ? (
                <LessonVideoPlayer key={`${previewLesson.id}:${previewLesson.videoMediaId || previewLesson.videoUrl || ''}`} lesson={previewLesson} />
              ) : (
                <p>{t('courseContent.lessons.noVideoUploaded')}</p>
              )}
            </div>
          </Modal>
        )}
        {videoLesson && (
          <Modal
            open
            onClose={closeVideoUpload}
            title={videoLesson.title}
            footer={(
              <button type="button" className="ds-btn ds-btn-secondary" onClick={closeVideoUpload}>
                {t('common.close')}
              </button>
            )}
          >
            <LessonVideoUpload lesson={videoLesson} onChange={handleVideoReady} />
          </Modal>
        )}
        {confirmDialog}
      </div>
    </FormErrorBoundary>
  );
}

export default CourseLessons;
